import { useMemo, useState } from 'react';
import { useApp } from '../App.jsx';
import { Card, ConfirmDialog, Dialog, Field, Icon, Money, MoneyField, MonthSelect, PersonDot, Seg, Toggle, useMoneyText } from './ui.jsx';
import { monthLabel, monthName, addMonths } from '../lib/calendar.js';
import { fmt, parsePercent, sum } from '../lib/money.js';
import { EXPENSE_CATEGORIES, versionAt } from '../lib/state.js';
import { addExpense, updateExpense, setExpenseAmountFrom, endExpense, deleteExpense, setAllowanceFrom, deleteAllowanceVersion, updateSettings, currentAmount } from '../lib/actions.js';

const SUGGESTIONS = [
  ['Rent', 'Housing'], ['Electricity', 'Utilities'], ['Water', 'Utilities'], ['Gas', 'Utilities'], ['Internet', 'Utilities'],
  ['Groceries', 'Groceries'], ['Phone', 'Utilities'], ['Transport', 'Transport'], ['Streaming', 'Subscriptions'], ['Gym', 'Health'],
  ['Home insurance', 'Insurance'], ['Car insurance', 'Insurance'],
];

const howOften = (e) => (e.every === 'month' ? 'Monthly' : e.every === 'year' ? `Yearly, ${monthName(e.dueMonth)}${e.spread ? ' (set aside monthly)' : ''}` : `Once, ${monthLabel(e.onMonth)}`);

function ExpenseDialog({ expense, preset, onClose }) {
  const { data, model, commit } = useApp();
  const isNew = !expense;
  const thisMonth = model.current < data.settings.startMonth ? data.settings.startMonth : model.current;
  const [name, setName] = useState(expense?.name || preset?.[0] || '');
  const [category, setCategory] = useState(expense?.category || preset?.[1] || 'Other');
  const [owner, setOwner] = useState(expense?.owner || 'joint');
  const [every, setEvery] = useState(expense?.every || 'month');
  const [dueMonth, setDueMonth] = useState(expense?.dueMonth || 1);
  const [spread, setSpread] = useState(!!expense?.spread);
  const [onMonth, setOnMonth] = useState(expense?.onMonth || thisMonth);
  const [from, setFrom] = useState(expense ? (expense.versions[0].from) : thisMonth);
  const [end, setEnd] = useState(expense?.end || null);
  const amount = useMoneyText(expense ? currentAmount(expense, thisMonth) : null, { allowNegative: false });
  const [amountFrom, setAmountFrom] = useState(thisMonth);
  const [error, setError] = useState(null);
  const amountChanged = expense && amount.parsed.ok && amount.parsed.value !== null && amount.parsed.value !== currentAmount(expense, amountFrom);

  const submit = () => {
    if (!amount.parsed.ok || amount.parsed.value === null) { setError('Enter an amount'); return; }
    const fields = { name, category, owner, every, ...(every === 'year' ? { dueMonth: Number(dueMonth), spread } : {}), ...(every === 'once' ? { onMonth } : {}), end };
    let res;
    if (isNew) {
      res = commit(`Add expense ${name || ''}`.trim(), (s) => addExpense(s, { ...fields, amount: amount.parsed.value, from }));
    } else {
      res = commit(`Edit expense ${name}`, (s) => {
        let next = updateExpense(s, expense.id, fields);
        if (amountChanged || (every === 'once')) next = setExpenseAmountFrom(next, expense.id, every === 'once' ? onMonth : amountFrom, amount.parsed.value);
        return next;
      });
    }
    if (res.error) setError(res.error);
    else onClose();
  };

  return (
    <Dialog
      title={isNew ? 'New expense' : `Edit ${expense.name}`}
      onClose={onClose}
      dirty={isNew ? !!name || amount.text !== '' : false}
      footer={<><span className="spacer" /><button type="button" className="btn" onClick={onClose}>Cancel</button><button type="button" className="btn primary" onClick={submit}>{isNew ? 'Add' : 'Save'}</button></>}
    >
      {error && <div className="banner danger" role="alert"><Icon name="alert" className="icon" />{error}</div>}
      <div className="grid2">
        <Field label="Name"><input className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus maxLength={80} placeholder="e.g. Rent" /></Field>
        <Field label="Category">
          <select className="input" value={category} onChange={(e) => setCategory(e.target.value)}>
            {[...new Set([...EXPENSE_CATEGORIES, category])].map((c) => <option key={c}>{c}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Who pays it" hint={owner === 'joint' ? 'Split between everyone, as set in "How joint expenses are split"' : 'Comes only out of this person\'s share'}>
        <select className="input" value={owner} onChange={(e) => setOwner(e.target.value)}>
          <option value="joint">Joint</option>
          {data.people.map((p) => <option key={p.id} value={p.id}>{p.name} only</option>)}
        </select>
      </Field>
      <Field label="How often" group>
        <Seg ariaLabel="How often" value={every} onChange={setEvery} options={[{ value: 'month', label: 'Every month' }, { value: 'year', label: 'Once a year' }, { value: 'once', label: 'Just once' }]} />
      </Field>
      {every === 'year' && (
        <div className="grid2">
          <Field label="Paid in">
            <select className="input" value={dueMonth} onChange={(e) => setDueMonth(Number(e.target.value))}>
              {Array.from({ length: 12 }, (_, i) => <option key={i + 1} value={i + 1}>{monthName(i + 1)}</option>)}
            </select>
          </Field>
          <Field label=" " hint="Takes 1/12 out of every month instead of all at once" group><Toggle checked={spread} onChange={setSpread} label="Set aside a bit every month" /></Field>
        </div>
      )}
      {every === 'once' && <Field label="Month" group><MonthSelect ariaLabel="Month" value={onMonth} onChange={(v) => v && setOnMonth(v)} /></Field>}
      <div className="grid2">
        <MoneyField label={every === 'year' ? 'Amount per year' : 'Amount'} m={amount} placeholder="0" />
        {!isNew && every !== 'once' && amountChanged && (
          <Field label="New amount applies from" hint={`Earlier months keep ${fmt(currentAmount(expense, addMonths(amountFrom, -1)))}`} group>
            <MonthSelect ariaLabel="New amount applies from" value={amountFrom} onChange={(v) => v && setAmountFrom(v)} />
          </Field>
        )}
      </div>
      {isNew && every !== 'once' && <Field label="Starts" group><MonthSelect ariaLabel="Starts" value={from} onChange={(v) => v && setFrom(v)} /></Field>}
      {every !== 'once' && (
        <Field label="Ends after" hint="Leave as “Doesn't end” for ongoing expenses" group>
          <MonthSelect ariaLabel="Ends after" value={end} onChange={setEnd} allowEmpty emptyLabel="Doesn't end" />
        </Field>
      )}
      {!isNew && expense.versions.length > 1 && (
        <details className="more">
          <summary>Amount history</summary>
          <table className="plain"><tbody>{expense.versions.map((v) => <tr key={v.from}><td>From {monthLabel(v.from)}</td><td><Money value={v.amount} /></td></tr>)}</tbody></table>
        </details>
      )}
    </Dialog>
  );
}

function AllowanceDialog({ person, onClose }) {
  const { model, commit, data } = useApp();
  const start = model.current < data.settings.startMonth ? data.settings.startMonth : model.current;
  const current = versionAt(person.allowance, start)?.amount ?? null;
  const m = useMoneyText(current, { allowNegative: false });
  const [from, setFrom] = useState(person.allowance.length ? start : data.settings.startMonth);
  const [error, setError] = useState(null);
  const submit = () => {
    if (!m.parsed.ok || m.parsed.value === null) { setError('Enter an amount (0 is fine)'); return; }
    const res = commit(`${person.name}: spending money ${fmt(m.parsed.value)} from ${monthLabel(from)}`, (s) => setAllowanceFrom(s, person.id, from, m.parsed.value));
    if (res.error) setError(res.error); else onClose();
  };
  return (
    <Dialog title={`${person.name}'s spending money`} onClose={onClose} footer={<><span className="spacer" /><button type="button" className="btn" onClick={onClose}>Cancel</button><button type="button" className="btn primary" onClick={submit}>Save</button></>}>
      <p className="dim">Money {person.name} keeps each month for personal things. It isn't tracked in detail; only spending beyond it is entered when reviewing a payday.</p>
      {error && <div className="banner danger" role="alert">{error}</div>}
      <div className="grid2">
        <MoneyField label="Per month" m={m} autoFocus placeholder="0" />
        <Field label="From" hint="Earlier months keep what they had" group><MonthSelect ariaLabel="From" value={from} onChange={(v) => v && setFrom(v)} /></Field>
      </div>
      {person.allowance.length > 0 && (
        <table className="plain">
          <thead><tr><th>From</th><th>Per month</th><th /></tr></thead>
          <tbody>
            {person.allowance.map((v) => (
              <tr key={v.id}>
                <td>{monthLabel(v.from)}</td><td><Money value={v.amount} /></td>
                <td><button type="button" className="btn ghost icon small" aria-label={`Remove the amount from ${monthLabel(v.from)}`} onClick={() => commit(`${person.name}: remove spending money from ${monthLabel(v.from)}`, (s) => deleteAllowanceVersion(s, person.id, v.id), { undoToast: true })}><Icon name="trash" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Dialog>
  );
}

function SplitCard() {
  const { data, commit, model } = useApp();
  const split = data.settings.split;
  const people = data.people.filter((p) => !p.until);
  const [custom, setCustom] = useState(() => Object.fromEntries(people.map((p) => [p.id, String(split.custom?.[p.id] ?? Math.round(100 / people.length))])));
  const parsed = Object.fromEntries(Object.entries(custom).map(([k, v]) => [k, parsePercent(v, { max: 100 })]));
  const cur = model.byKey[model.current] || model.months[0];
  const setMode = (mode) => {
    const next = { ...split, mode };
    if (mode === 'custom') next.custom = Object.fromEntries(Object.entries(parsed).map(([k, p]) => [k, p.ok ? p.value : 0]));
    commit(`Split joint expenses: ${mode === 'equal' ? 'equally' : mode === 'income' ? 'by income' : 'custom'}`, (s) => updateSettings(s, { split: next }));
  };
  return (
    <Card title="How joint expenses are split">
      <Seg ariaLabel="Split" value={split.mode} onChange={setMode} options={[{ value: 'income', label: 'By take-home pay' }, { value: 'equal', label: 'Equally' }, { value: 'custom', label: 'Custom %' }]} />
      <p className="small muted" style={{ marginTop: 8 }}>
        {split.mode === 'income' && 'Each pays in proportion to what they bring home that month, so a smaller salary pays a smaller share.'}
        {split.mode === 'equal' && 'Everyone pays the same share.'}
        {split.mode === 'custom' && 'Fixed percentages.'}
      </p>
      {split.mode === 'custom' && (
        <div className="row wrap" style={{ marginTop: 8 }}>
          {people.map((p) => (
            <Field key={p.id} label={p.name} error={!parsed[p.id]?.ok ? parsed[p.id]?.error : null}>
              <div className="input-wrap" style={{ width: 110 }}>
                <input className="input money" value={custom[p.id] ?? ''} onChange={(e) => setCustom({ ...custom, [p.id]: e.target.value })}
                  onBlur={() => { if (Object.values(parsed).every((x) => x.ok)) setMode('custom'); }} aria-label={`${p.name} share`} />
                <span className="suffix">%</span>
              </div>
            </Field>
          ))}
        </div>
      )}
      {cur && (
        <div className="row wrap small" style={{ marginTop: 10 }}>
          <span className="muted">In {monthLabel(cur.key)}:</span>
          {cur.people.map((e) => <span key={e.personId} className="chip"><PersonDot color={e.color} size={8} />{e.name} {Math.round((cur.shares[e.personId] || 0) * 100)}%</span>)}
        </div>
      )}
    </Card>
  );
}

export default function Budget() {
  const { data, model, commit } = useApp();
  const [editing, setEditing] = useState(null); // expense | 'new' | ['new', preset]
  const [allowanceFor, setAllowanceFor] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [ending, setEnding] = useState(null);
  const [endMonth, setEndMonth] = useState(model.current);
  const month = model.current < data.settings.startMonth ? data.settings.startMonth : model.current;
  const mm = model.byKey[month];
  const nameOf = (id) => (id === 'joint' ? 'Joint' : data.people.find((p) => p.id === id)?.name || '?');
  const used = new Set(data.expenses.map((e) => e.name.toLowerCase()));

  const totals = useMemo(() => {
    if (!mm) return null;
    const personal = sum(mm.people.map((e) => e.expected.personal));
    const income = sum(mm.people.map((e) => e.expected.income));
    return { personal, income, joint: mm.jointBudget, own: sum(mm.people.map((e) => e.expected.own)), saved: mm.expectedSavings };
  }, [mm]);

  const active = data.expenses.filter((e) => !e.end || e.end >= month);
  const ended = data.expenses.filter((e) => e.end && e.end < month);

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="page-head">
        <div>
          <h1>Budget</h1>
          <p>What you need every month. Whatever is left goes to savings.</p>
        </div>
      </div>

      {totals && (
        <div className="grid3">
          <div className="tile"><div className="label">Take-home in {monthLabel(month)}</div><div className="value num">{fmt(totals.income)}</div><div className="sub">{data.settings.countMealCard ? 'with the meal card' : 'meal card and reimbursements not counted'}</div></div>
          <div className="tile"><div className="label">Expenses</div><div className="value num">{fmt(totals.joint + totals.own)}</div><div className="sub">joint {fmt(totals.joint)}{totals.own ? ` · personal fixed ${fmt(totals.own)}` : ''}</div></div>
          <div className="tile"><div className="label">Spending money</div><div className="value num">{fmt(totals.personal)}</div><div className="sub">{mm.people.map((e) => `${e.name} ${fmt(e.expected.personal)}`).join(' · ')}</div></div>
          <div className="tile"><div className="label">Left for savings</div><div className="value num" style={{ color: totals.saved < 0 ? 'var(--danger)' : undefined }}>{fmt(totals.saved)}</div><div className="sub">{totals.saved < 0 ? 'the budget is more than what comes in' : 'expected this month'}</div></div>
        </div>
      )}

      <Card title="Expenses" actions={<button type="button" className="btn primary small" onClick={() => setEditing('new')}><Icon name="plus" />Add expense</button>}>
        {!data.expenses.length && <p className="muted" style={{ marginBottom: 10 }}>No expenses yet. Quick add:</p>}
        <div className="row wrap" style={{ marginBottom: data.expenses.length ? 10 : 0 }}>
          {SUGGESTIONS.filter(([n]) => !used.has(n.toLowerCase())).slice(0, data.expenses.length ? 6 : 12).map((s) => (
            <button key={s[0]} type="button" className="chip" style={{ cursor: 'pointer' }} onClick={() => setEditing(['new', s])}>+ {s[0]}</button>
          ))}
        </div>
        {active.length > 0 && (
          <div className="table-wrap" style={{ border: 0 }}>
            <table className="plain">
              <thead><tr><th>Expense</th><th className="left">Paid by</th><th className="left hide-sm">How often</th><th>{monthLabel(month, { short: true })}</th><th className="hide-sm">Amount</th><th /></tr></thead>
              <tbody>
                {active.map((e) => (
                  <tr key={e.id}>
                    <td><div style={{ fontWeight: 560 }}>{e.name}</div><div className="small muted">{e.category}{e.versions[0].from > month ? ` · starts ${monthLabel(e.versions[0].from)}` : ''}{e.end ? ` · until ${monthLabel(e.end)}` : ''}</div></td>
                    <td className="left">{e.owner === 'joint' ? 'Joint' : <span className="pill-person"><PersonDot color={data.people.find((p) => p.id === e.owner)?.color} />{nameOf(e.owner)}</span>}</td>
                    <td className="left hide-sm dim">{howOften(e)}</td>
                    <td><Money value={mm?.expenses.find((x) => x.id === e.id)?.budget ?? 0} /></td>
                    <td className="hide-sm dim"><Money value={currentAmount(e, month)} />{e.every === 'year' ? '/yr' : ''}</td>
                    <td>
                      <div className="row" style={{ justifyContent: 'flex-end', gap: 2 }}>
                        <button type="button" className="btn ghost icon small" aria-label={`Edit ${e.name}`} onClick={() => setEditing(e)}><Icon name="edit" /></button>
                        {e.every !== 'once' && <button type="button" className="btn ghost small" onClick={() => { setEndMonth(month); setEnding(e); }}>Stop</button>}
                        <button type="button" className="btn ghost icon small" aria-label={`Delete ${e.name}`} onClick={() => setDeleting(e)}><Icon name="trash" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {ended.length > 0 && (
          <details className="more" style={{ marginTop: 10 }}>
            <summary>Stopped ({ended.length})</summary>
            {ended.map((e) => (
              <div className="list-row" key={e.id}>
                <span>{e.name}</span><span className="small muted">ended {monthLabel(e.end)}</span><span className="spacer" />
                <button type="button" className="btn small" onClick={() => commit(`Restart ${e.name}`, (s) => updateExpense(s, e.id, { end: null }), { undoToast: true })}>Restart</button>
                <button type="button" className="btn ghost icon small" aria-label={`Delete ${e.name}`} onClick={() => setDeleting(e)}><Icon name="trash" /></button>
              </div>
            ))}
          </details>
        )}
      </Card>

      <div className="grid2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
        <Card title="Personal spending money">
          <p className="small muted" style={{ marginBottom: 8 }}>What each person keeps each month for themselves: everything else they bring home goes to joint expenses and savings.</p>
          {data.people.filter((p) => !p.until || p.until >= month).map((p) => {
            const v = versionAt(p.allowance, month);
            return (
              <div className="list-row" key={p.id}>
                <span className="pill-person"><PersonDot color={p.color} />{p.name}</span>
                <span className="spacer" />
                {v ? <Money value={v.amount} /> : <span className="muted">not set</span>}<span className="small muted">/ month</span>
                <button type="button" className="btn small" onClick={() => setAllowanceFor(p)}>{v ? 'Change' : 'Set'}</button>
              </div>
            );
          })}
        </Card>
        <SplitCard />
      </div>

      {editing && <ExpenseDialog expense={editing === 'new' || Array.isArray(editing) ? null : editing} preset={Array.isArray(editing) ? editing[1] : null} onClose={() => setEditing(null)} />}
      {allowanceFor && <AllowanceDialog person={data.people.find((p) => p.id === allowanceFor.id)} onClose={() => setAllowanceFor(null)} />}
      {deleting && (
        <ConfirmDialog
          title={`Delete ${deleting.name}?`}
          message={`It disappears from every month, past ones too. To keep the past and stop it from now on, use “Stop” instead.`}
          confirmLabel="Delete"
          danger
          onCancel={() => setDeleting(null)}
          onConfirm={() => { commit(`Delete expense ${deleting.name}`, (s) => deleteExpense(s, deleting.id), { undoToast: true }); setDeleting(null); }}
        />
      )}
      {ending && (
        <ConfirmDialog title={`Stop ${ending.name}`} message="It stays in the months up to this one, and isn't budgeted after." confirmLabel="Stop it" onCancel={() => setEnding(null)}
          onConfirm={() => { const r = commit(`Stop ${ending.name} after ${monthLabel(endMonth)}`, (s) => endExpense(s, ending.id, endMonth), { undoToast: true }); if (!r.error) setEnding(null); }}>
          <Field label="Last month it's paid" group><MonthSelect ariaLabel="Last month it's paid" value={endMonth} onChange={(v) => v && setEndMonth(v)} /></Field>
        </ConfirmDialog>
      )}
    </div>
  );
}
